import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import {
  useSupabaseClient,
  useSupabaseReady,
} from '../supabase/SupabaseProvider';
import type { Message } from '../types';

const PAGE_SIZE = 25;

interface UseConversationMessagesResult {
  messages: Message[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useConversationMessages(
  conversationId: string | null | undefined,
): UseConversationMessagesResult {
  const client = useSupabaseClient();
  const ready = useSupabaseReady();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const oldestCursorRef = useRef<string | null>(null);

  const fetchPage = useCallback(
    async (cursor: string | null): Promise<Message[]> => {
      if (!client || !conversationId) return [];
      // Chat detail is the direct-message surface — broadcasts belong in
      // the announcements list. Filter at the SQL layer so the message
      // thread never paginates through broadcast fanout rows.
      let q = client
        .from('messages')
        .select('*, attachments:message_attachments(*)')
        .eq('conversation_id', conversationId)
        .neq('message_type', 'broadcast')
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);
      if (cursor) {
        q = q.lt('created_at', cursor);
      }
      const { data, error: err } = await q;
      if (err) throw err;
      return (data as Message[]) ?? [];
    },
    [client, conversationId],
  );

  const refresh = useCallback(async () => {
    if (!client || !ready || !conversationId) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchPage(null);
      setMessages(rows);
      oldestCursorRef.current =
        rows.length > 0 ? rows[rows.length - 1].created_at : null;
      setHasMore(rows.length === PAGE_SIZE);
    } catch (err: unknown) {
      Sentry.captureException(err, {
        tags: { feature: 'comms', action: 'fetch_messages' },
      });
      setError((err as Error)?.message ?? 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [client, ready, conversationId, fetchPage]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || loading) return;
    setLoadingMore(true);
    try {
      const rows = await fetchPage(oldestCursorRef.current);
      setMessages((prev) => [...prev, ...rows]);
      if (rows.length > 0) {
        oldestCursorRef.current = rows[rows.length - 1].created_at;
      }
      setHasMore(rows.length === PAGE_SIZE);
    } catch (err: unknown) {
      Sentry.captureException(err, {
        tags: { feature: 'comms', action: 'fetch_messages_more' },
      });
      setError((err as Error)?.message ?? 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }, [fetchPage, hasMore, loading, loadingMore]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    // Gate on `ready` — same as useAdminConversations. Subscribing before
    // the JWT is applied via realtime.setAuth() joins the socket
    // unauthenticated, so Realtime RLS silently drops every
    // postgres_changes event and the channel stays deaf until something
    // re-subscribes (conversation switch / manual refresh). That race is
    // why the sidebar updated live but the open thread didn't (GRAVY-10213).
    if (!client || !ready || !conversationId) return;

    // supabase-js caches realtime channels by name. React StrictMode
    // double-invokes effects; the 2nd mount happens before the 1st
    // mount's async `removeChannel` cleanup resolves, so `.channel(name)`
    // can return the still-subscribed channel and `.on(…)` after
    // `.subscribe()` throws "cannot add postgres_changes callbacks
    // after subscribe()". Force-evict any stale channel before binding.
    const channelName = `messages:${conversationId}`;
    const existing = client
      .getChannels()
      .find((c) => c.topic === `realtime:${channelName}`);
    if (existing) void client.removeChannel(existing);

    const channel = client.channel(channelName).on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`,
      },
      async (payload) => {
        const incoming = payload.new as Message;
        // Broadcasts surface in the announcements list, not the chat
        // detail — skip them here so a fanout doesn't bubble into the
        // message thread.
        if (incoming.message_type === 'broadcast') return;
        // Image messages also need their attachments row, which the
        // realtime payload doesn't include. Hydrate it before merging
        // so the bubble doesn't render blank until the next refresh.
        if (incoming.message_type === 'image') {
          try {
            const { data } = await client
              .from('message_attachments')
              .select('*')
              .eq('message_id', incoming.id);
            if (data && data.length > 0) {
              (incoming as Message & { attachments?: unknown[] }).attachments =
                data;
            }
          } catch (err) {
            Sentry.captureException(err, {
              tags: { feature: 'comms', action: 'hydrate_image_attachment' },
              extra: { message_id: incoming.id },
            });
          }
        }
        setMessages((prev) => {
          if (prev.some((m) => m.id === incoming.id)) return prev;
          return [incoming, ...prev];
        });
      },
    );
    channel.subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [client, ready, conversationId]);

  return { messages, loading, loadingMore, error, hasMore, loadMore, refresh };
}
